import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { listPublicLibraries } from "@/apps/rag/api/rag-api.js";
import { mapPublicLibrary } from "@/apps/rag/api/mappers.js";
import { LibraryFolderTile } from "@/apps/rag/components/LibraryFolderTile.js";
import { RagEmptyState } from "@/apps/rag/components/RagEmptyState.js";
import { RagPageHeader } from "@/apps/rag/components/RagPageHeader.js";
import { RagPermissionNote } from "@/apps/rag/components/RagPermissionNote.js";
import type { RagLibrary } from "@/apps/rag/types.js";
import { publicLibraryDocumentsPath } from "../paths.js";

export function RegulatoryLibrariesPage() {
  const [libraries, setLibraries] = useState<RagLibrary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { libraries: data } = await listPublicLibraries();
      setLibraries(data.map(mapPublicLibrary));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load public libraries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredLibraries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return libraries;
    return libraries.filter(
      (library) =>
        library.name.toLowerCase().includes(query) ||
        library.description.toLowerCase().includes(query)
    );
  }, [libraries, search]);

  if (loading) {
    return (
      <section className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </section>
    );
  }

  if (error) {
    return (
      <section className="flex flex-1 flex-col items-center justify-center gap-3 p-6">
        <p className="text-sm text-red-400">{error}</p>
        <Button variant="outline" onClick={() => void load()}>
          Retry
        </Button>
      </section>
    );
  }

  return (
    <section className="flex flex-1 flex-col p-6">
      <RagPageHeader
        icon={Scale}
        title="Regulatory Libraries"
        action={
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search public libraries…"
            className="w-full border-white/10 bg-slate-900/75 sm:w-[270px]"
            aria-label="Search public libraries"
          />
        }
      />

      <RagPermissionNote variant="info">
        Public libraries are managed by administrators. You can browse documents and start chats.
      </RagPermissionNote>

      {!filteredLibraries.length ? (
        <RagEmptyState
          icon={Scale}
          title={libraries.length ? "No libraries match your search" : "No public libraries yet"}
          description={
            libraries.length
              ? "Try a different search term."
              : "Check back later for curated regulatory content."
          }
        />
      ) : (
        <div className="mb-8 flex flex-wrap gap-x-6 gap-y-8">
          {filteredLibraries.map((library) => (
            <LibraryFolderTile
              key={library.id}
              to={publicLibraryDocumentsPath(library.id)}
              title={library.name}
            />
          ))}
        </div>
      )}
    </section>
  );
}
